import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConnectorAction, useConnectors } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { ConnectionBlock } from '../settings/Integrations.tsx';

const RESULT_MESSAGES: Record<string, string> = {
  connected: 'Calendario dello spazio collegato',
  denied: 'Collegamento annullato: nessun permesso concesso',
  expired: 'Il collegamento è scaduto: riprova',
  error: 'Collegamento non riuscito: riprova',
};

/** Lets whoever manages a workspace connect a Google calendar just for that workspace —
 * separate from a person's own calendar or the company-wide one in Impostazioni → Integrazioni. */
export function WorkspaceCalendarSection({ workspaceId, canManage }: { workspaceId: string; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const connectors = useConnectors(orgId);
  const action = useConnectorAction(orgId);
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const r = params.get('google');
    if (r) {
      toast(RESULT_MESSAGES[r] ?? 'Operazione completata');
      setParams((p) => { const n = new URLSearchParams(p); n.delete('google'); return n; }, { replace: true });
    }
  }, [params, setParams, toast]);

  if (!connectors.data) return null;
  const google = connectors.data.find((c) => c.key === 'google_calendar');
  if (!google) return null;
  const connection = google.connections.find((c) => c.scope === 'workspace' && c.workspaceId === workspaceId);
  if (!connection && !canManage) return null; // nothing to show and nothing this person could set up

  return (
    <section className="section" style={{ marginTop: 20 }}>
      <h3 className="subhead">Calendario dello spazio</h3>
      {!google.configured ? (
        <p className="hint">Google Calendar non è ancora configurato su questo server.</p>
      ) : (
        <div className="card" style={{ padding: '0 16px' }}>
          <ConnectionBlock
            title="Calendario di questo spazio"
            description="Un calendario Google dedicato a questo spazio, indipendente da quello personale o aziendale."
            connection={connection}
            onConnect={canManage ? () => action.mutate({ action: 'authorize', scope: 'workspace', workspaceId }, {
              onSuccess: (r) => { if (r && 'url' in r) window.location.assign(r.url); },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Impossibile avviare il collegamento'),
            }) : undefined}
            busy={action.isPending}
          />
        </div>
      )}
    </section>
  );
}
